import React, { useState } from 'react';
import { Box, Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Divider, Tabs, Tab, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { Slide } from '@mui/material';
import { SlideProps } from '@mui/material/Slide';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { alpha } from '@mui/material/styles';
import { addMinutes, addHours, addDays, format, formatDistanceToNow, differenceInMinutes, startOfDay, isToday } from 'date-fns';

// Icons
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AlarmIcon from '@mui/icons-material/Alarm';
import AlarmOnIcon from '@mui/icons-material/AlarmOn';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PauseIcon from '@mui/icons-material/Pause';

import { useAppDispatch } from 'src/store';
import { updateMessage } from 'src/features/account/messages.slice';
import { useSnackbar } from 'notistack';
import { useColorMode } from 'src/theme/ThemeProvider';
import { styled } from '@mui/material/styles';

interface RescheduleDeliveryProps {
  message: any;
  channelId?: string;
  conversationDeliveryState?: string;
}

const TimePresetChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
  color: theme.palette.text.primary,
  fontWeight: 400,
  boxShadow: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    boxShadow: `0 1px 3px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

const SelectedTimePresetChip = styled(TimePresetChip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.15),
  border: `1px solid ${theme.palette.primary.main}`,
  color: theme.palette.primary.main,
  fontWeight: 600,
  boxShadow: `0 1px 3px ${alpha(theme.palette.primary.main, 0.3)}`,
}));

// Custom Transition for Dialog
const TransitionUp = React.forwardRef(function TransitionUp(
  props: SlideProps & { children: React.ReactElement<any, any>; },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const RescheduleDelivery: React.FC<RescheduleDeliveryProps> = ({ message, channelId, conversationDeliveryState = '' }) => {
  const theme = useTheme();
  const { mode } = useColorMode();
  const isDarkMode = mode === 'dark';
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Local states
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<Date | null>(message.deliver_after ? new Date(message.deliver_after) : addMinutes(new Date(), 30));
  const [dateError, setDateError] = useState('');
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [rescheduleMode, setRescheduleMode] = useState<'quick' | 'custom'>('quick');
  const [suggestedTimes, setSuggestedTimes] = useState<{ label: string; date: Date; highlight?: boolean }[]>([]);
  const [saveInProgress, setSaveInProgress] = useState(false);

  const generateSuggestedTimes = () => {
    const now = new Date();
    const nowHour = now.getHours();
    const suggestions: { label: string; date: Date; highlight?: boolean }[] = [];

    // Basic incremental times
    suggestions.push({ label: '+15 min', date: addMinutes(now, 15) });
    suggestions.push({ label: '+30 min', date: addMinutes(now, 30) });
    suggestions.push({ label: '+1 hour', date: addHours(now, 1) });

    // Smart time slots based on time of day
    if (nowHour < 9) {
      const morning = new Date(now);
      morning.setHours(9, 0, 0, 0);
      suggestions.push({ label: '9:00 AM', date: morning, highlight: true });
    } else if (nowHour < 12) {
      const noon = new Date(now);
      noon.setHours(12, 0, 0, 0);
      suggestions.push({ label: 'Noon', date: noon, highlight: true });
    } else if (nowHour < 17) {
      const eod = new Date(now);
      eod.setHours(17, 0, 0, 0);
      suggestions.push({ label: '5:00 PM', date: eod, highlight: true });
    } else {
      const tomorrow = addDays(now, 1);
      tomorrow.setHours(9, 0, 0, 0);
      suggestions.push({ label: 'Tomorrow 9 AM', date: tomorrow, highlight: true });
    }

    // Always add tomorrow morning
    const tomorrowMorning = addDays(startOfDay(now), 1);
    tomorrowMorning.setHours(9, 0, 0, 0);
    if (!suggestions.find((s) => s.label === 'Tomorrow 9 AM')) {
      suggestions.push({ label: 'Tomorrow 9 AM', date: tomorrowMorning });
    }

    // Early evening option for later in the day
    if (nowHour >= 15) {
      const evening = new Date(now);
      evening.setHours(18, 0, 0, 0);
      suggestions.push({ label: 'Tonight 6 PM', date: evening });
    }

    // Weekend handling (if today is Friday, offer Monday morning)
    if (now.getDay() === 5) {
      const monday = addDays(startOfDay(now), 3);
      monday.setHours(9, 0, 0, 0);
      suggestions.push({ label: 'Monday 9 AM', date: monday });
    }

    setSuggestedTimes(suggestions);
  };

  const handleRescheduleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    generateSuggestedTimes();
    setRescheduleOpen(true);
  };

  const handleRescheduleSave = () => {
    if (!rescheduleDate) {
      setDateError('Please select a delivery time');
      return;
    }

    if (rescheduleDate <= new Date()) {
      setDateError('Delivery time must be in the future');
      return;
    }

    setSaveInProgress(true);

    dispatch(updateMessage({
      messageId: message.id,
      channelId: channelId,
      deliver_after: rescheduleDate.toISOString(),
      content: message.content,
    }))
      .unwrap()
      .then(() => {
        enqueueSnackbar(`Delivery rescheduled for ${format(rescheduleDate, 'EEE, MMM do, h:mm a')}`, {
          variant: 'success',
          autoHideDuration: 5000,
        });
        setRescheduleOpen(false);
      })
      .catch(() => {
        enqueueSnackbar('Failed to reschedule', { variant: 'error' });
      })
      .finally(() => {
        setSaveInProgress(false);
      });
  };

  // Render nothing if message not pending
  if (message.status !== 'pending_delivery') return null;

  // Format the label based on screen size
  const formatChipLabel = () => {
    const date = rescheduleDate || new Date();
    const isScheduledToday = isToday(date);
    
    if (conversationDeliveryState === 'pause') {
      if (isMobile) {
        // On mobile, show very compact format
        if (!isScheduledToday) {
          return `Paused • ${format(date, 'MMM do')}`;
        }
        return `Paused`;
      }
      // On desktop, always show full date with ordinal
      return `Paused • ${format(date, 'MMM do, h:mm a')}`;
    }
    
    // For non-paused state
    if (!isScheduledToday) {
      // Show date with ordinal when not today
      if (isMobile) {
        return format(date, 'MMM do, h:mm a');
      }
      return format(date, 'MMM do, h:mm a');
    }
    
    // For today, just show time
    return format(date, 'h:mm a');
  };

  return (
    <>
      {/* Chip */}
      <Tooltip title={
        conversationDeliveryState === 'pause' 
          ? `Paused until ${format(rescheduleDate || new Date(), 'MMM do, h:mm a')} – click to reschedule` 
          : 'Click to reschedule delivery time'
      }>
        <Chip
          clickable
          onClick={handleRescheduleClick}
          icon={
            conversationDeliveryState === 'pause' ? (
              <PauseIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
            ) : (
              <AlarmIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
            )
          }
          label={formatChipLabel()}
          size="small"
          sx={{
            mt: 0.5,
            alignSelf: 'flex-start',
            borderRadius: 4,
            px: isMobile ? 0 : 0.5,
            height: isMobile ? 22 : 24,
            maxWidth: isMobile ? '100px' : 'none',
            '& .MuiChip-label': { 
              px: isMobile ? 0.5 : 1,
              fontSize: isMobile ? '0.7rem' : '0.8125rem',
            },
            bgcolor: (theme) =>
              conversationDeliveryState === 'pause'
                ? alpha(theme.palette.warning.main, 0.12)
                : alpha(theme.palette.primary.main, 0.1),
            color:
              conversationDeliveryState === 'pause' ? 'warning.main' : 'primary.main',
            border: (theme) =>
              `1px solid ${
                conversationDeliveryState === 'pause'
                  ? alpha(theme.palette.warning.main, 0.3)
                  : alpha(theme.palette.primary.main, 0.3)
              }`,
            '&:hover': {
              bgcolor: (theme) =>
                conversationDeliveryState === 'pause'
                  ? alpha(theme.palette.warning.main, 0.18)
                  : alpha(theme.palette.primary.main, 0.15),
            },
          }}
        />
      </Tooltip>

      {/* Dialog */}
      <Dialog
        open={rescheduleOpen}
        onClose={() => setRescheduleOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.common.black, 0.2)}`,
            overflow: 'visible',
          },
        }}
        TransitionComponent={TransitionUp}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleRescheduleSave();
          }
        }}
      >
        <DialogTitle
          sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6">Reschedule Delivery</Typography>
          </Box>

          <Tabs
            value={selectedTabIndex}
            onChange={(e, newIndex) => {
              setSelectedTabIndex(newIndex);
              setRescheduleMode(newIndex === 0 ? 'quick' : 'custom');
            }}
            sx={{ minHeight: 0 }}
          >
            <Tab icon={<AlarmIcon fontSize="small" />} iconPosition="start" label="Quick" sx={{ minHeight: 0, py: 1 }} />
            <Tab icon={<CalendarMonthIcon fontSize="small" />} iconPosition="start" label="Custom" sx={{ minHeight: 0, py: 1 }} />
          </Tabs>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2, pb: 3 }}>
          {/* Quick mode */}
          {rescheduleMode === 'quick' && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Select when to deliver this message
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                {suggestedTimes.map((suggestion, index) => {
                  const isSelected =
                    rescheduleDate && differenceInMinutes(rescheduleDate, suggestion.date) === 0;
                  const ChipComponent = isSelected ? SelectedTimePresetChip : TimePresetChip;
                  return (
                    <ChipComponent
                      key={index}
                      label={suggestion.label}
                      clickable
                      onClick={() => {
                        setRescheduleDate(suggestion.date);
                        setDateError('');
                      }}
                      icon={suggestion.highlight ? <AlarmOnIcon fontSize="small" /> : undefined}
                      sx={{ transform: suggestion.highlight ? 'scale(1.05)' : 'none' }}
                    />
                  );
                })}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  borderRadius: 1.5,
                  p: 1.5,
                  gap: 1,
                }}
              >
                <NotificationsIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Will be delivered:
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {rescheduleDate ? format(rescheduleDate, 'EEE, MMM do, h:mm a') : 'Select a time'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Custom mode */}
          {rescheduleMode === 'custom' && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ pt: 1 }}>
                <DateTimePicker
                  label="Delivery Time"
                  value={rescheduleDate}
                  onChange={(newVal) => {
                    setRescheduleDate(newVal as Date);
                    setDateError('');
                  }}
                  minDateTime={new Date()}
                  slotProps={{ textField: { fullWidth: true, helperText: 'Select exact date and time for delivery' } }}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ mt: 3, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Your local timezone: <strong>{Intl.DateTimeFormat().resolvedOptions().timeZone}</strong>
                  </Typography>

                  {rescheduleDate && (
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'primary.main' }}>
                      {format(rescheduleDate, 'EEEE, MMMM do')} at {format(rescheduleDate, 'h:mm a')}
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({formatDistanceToNow(rescheduleDate, { addSuffix: true })})
                      </Typography>
                    </Typography>
                  )}
                </Box>
              </Box>
            </LocalizationProvider>
          )}

          {dateError && (
            <Typography color="error" variant="body2" sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventBusyIcon fontSize="small" />
              {dateError}
            </Typography>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRescheduleOpen(false)} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRescheduleSave}
            disabled={saveInProgress}
            startIcon={saveInProgress ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Schedule Delivery
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RescheduleDelivery; 
